import * as React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'utils/date';
import MoneyView from 'shared/components/MoneyView';
import Profit from 'components/Profit';
import CurrentPrice from 'components/CurrentPrice';
import { getEarliestStart } from 'utils/billingPeriod';
import { IdeaTopCardFragment } from '../../../../__generated__/IdeaTopCardFragment';
import { Factoid } from '../Factoid';

interface Props {
  idea: IdeaTopCardFragment;
  isIdeaLandingCard?: boolean;
}

export const Properties: React.FC<Props> = ({ idea, isIdeaLandingCard }) => {
  const { t } = useTranslation();
  const { properties } = idea;
  switch (properties.__typename) {
    case 'Investment_LimitedIdeaProperties': {
      const { finishRequestDate, period } = properties;
      return (
        <>
          <Factoid title={t('Income forecast')} data-testid="profit">
            <Profit>{idea.profit}</Profit>
          </Factoid>
          {isIdeaLandingCard && (
            <Factoid title={t('Min amount')} data-testid="min-amount">
              <MoneyView precision={0}>{idea.enterAmount}</MoneyView>
            </Factoid>
          )}
          <Factoid title={t('Requests up to')} data-testid="finish-request-date">
            {finishRequestDate ? formatDate(moment(finishRequestDate), true) : t('not yet known')}
          </Factoid>
          <Factoid title={t('Period')} data-testid="period">
            {period ? t('{{count}} days', { count: period }) : t('not yet known')}
          </Factoid>
        </>
      );
    }

    case 'Investment_UnlimitedIdeaProperties': {
      const { billingPeriod } = properties;
      return (
        <>
          <Factoid title={t('Income forecast')} data-testid="profit">
            <Profit>{idea.profit}</Profit>
          </Factoid>
          {isIdeaLandingCard && (
            <Factoid title={t('Min amount')} data-testid="min-amount">
              <MoneyView precision={0}>{idea.enterAmount}</MoneyView>
            </Factoid>
          )}
          <Factoid title={t('Earl start')} data-testid="start-date">
            {billingPeriod ? formatDate(getEarliestStart(billingPeriod)) : t('not yet known')}
          </Factoid>
          <Factoid title={t('Minimum period')} data-testid="period">
            {t(billingPeriod)}
          </Factoid>
        </>
      );
    }

    case 'Investment_BuyAndHoldIdeaProperties': {
      const { manualStartDate, manualDurationInMonths } = properties;
      return (
        <>
          <Factoid title={t('Current price')} data-testid="current-price">
            <CurrentPrice>{idea.currentPrice}</CurrentPrice>
          </Factoid>
          {isIdeaLandingCard && (
            <Factoid title={t('Min amount')} data-testid="min-amount">
              <MoneyView precision={0}>{idea.enterAmount}</MoneyView>
            </Factoid>
          )}
          <Factoid title={t('Start')} data-testid="start-date">
            {manualStartDate ? formatDate(moment(manualStartDate), true) : t('At any time')}
          </Factoid>
          <Factoid title={t('Minimum period')} data-testid="period">
            {manualDurationInMonths
              ? t('{{count}} months', { count: manualDurationInMonths })
              : t('None')}
          </Factoid>
        </>
      );
    }
  }
};
